import React, { useEffect, useMemo, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import TableContextProvider, { useTableContext, useTableControls } from 'common/components/table/table-context';
import { eiqSecureGuestInitialColumns, guestColumns } from 'common/list-configs/guest-list.config';
import { Breadcrumb, Button, Checkbox, Col, Form, Input, QRCode, Row, Select, Space, Switch } from 'antd';
import TableBase from 'common/components/table/table-base';
import eiqSecureStore from '../../stores/eiq-secure.store';
import { ReactComponent as AdmitIcon } from 'assets/icons/admit.svg';
import { ReactComponent as KeyIcon } from 'assets/icons/collect-key.svg';
import { ReactComponent as BackIcon } from 'assets/icons/back.svg';
import { ReactComponent as TurnbackIcon } from 'assets/icons/turnback.svg';
import { ReactComponent as PhoneIcon } from 'assets/icons/phone.svg';
import { FormWrapper } from '../../../../common/components/form-wrapper/form-wrapper';
import './eiq-secure-gust-license.scss';
import guestService from '../../../../common/services/guest-service/guest.service';
import { notification } from '../../../../common/utils/notification';
import { formatAddressLabel } from '../../../../common/helpers/address.helper';
import { useModal } from '../../../../common/hooks/use-modal/use-modal.hook';
import TurnbackModal from '../turnback-modal/turnback-modal';
import ContactsModal from '../contacts-modal/contacts-modal';
import KeyActionModal from '../key-action-modal/key-action-modal-modal';
import configurationStore from 'common/stores/configuration/configuration.store';
import userAuthStore from 'common/stores/user-auth/user-auth.store';
import residentsService from '../../../../common/services/residents-service/residents.service';
import addressesKeyService from '../../../../common/services/addresses-key-service/addresses-key.service';
import InputPhoneFormItem from '../../../../common/components/form-elements/input-phone/form-item/input-phone-form-item';
import { eiqSecureGuestLicenseValidation } from './eiq-secure-guest-license.validation';
import { entryTypeMap, toGateOptions } from 'features/eiq-secure/constans/common.eiq-secure';
import SelectState from 'common/components/form-elements/select-state/select-state';
import { TurnBack, Admit } from 'features/eiq-secure/constans/admit-actions.constants';
import { useMedia } from '../../../../common/hooks/use-media/use-media.hook';
import { AdmitGuestEnum } from '../../constans/admit-types';
import Footer from '../../../../common/components/footer/footer';
import { setResidentListSearchFilter } from 'common/helpers/search-filter.helper';
import EiqSecureGuestPrintInstruction from './eiq-secure-guest-print-instruction';
import { ReactComponent as PrintIcon } from 'assets/icons/print.svg';
import { useJsApiLoader } from '@react-google-maps/api';
import { isServiceGuest } from 'common/helpers/guest-type.helpers';
import { IGuestRead } from 'common/services/residents-service/residents.service.types';
import { EntryType } from 'common/enums/entry-type.enum';
import plateRecognitionContext from 'features/eiq-secure/stores/license-plate-recognition-context';
import { Image } from 'antd';
import { useDefaultAdmitValues } from 'features/eiq-secure/hooks/use-default-admit-values/use-default-admit-values.hook';
import { useBoundPlateRecognition } from 'features/eiq-secure/hooks/use-bound-plate-recognition/use-bound-plate-recognition.hook';
import { filterColumnsByPersonalization } from 'common/components/table/table-helpers';
import { EIQ_SECURE_GUEST_LIST_TABLE_NAME } from '../eiq-secure-guests-list/eiq-secure-guest-list';
import { IIdDocumentData } from 'common/components/id-capture/scandit-id-capture.types';
import { base64ToFile } from 'common/helpers/getBase64.helper';
import accountService from 'common/services/file-service/file.service';
import { UploadFileTypes } from 'common/services/file-service/file.service.types';
import { IAddIdDocument } from 'common/services/guest-service/guest.service.types';
import featureStore from 'common/stores/features/feature.store';
import fastAccessService from 'common/services/fast-access-service/fast-access-service';
import citationService from 'common/services/citation-service/citation.service';
import { trackPromise } from 'react-promise-tracker';
import DrivingPrivilegesRevokeModal from '../driving-privileges-revoked-modal/driving-privileges-revoked-modal';
import DriveId from '../drive-id/drive-id';
import { reaction } from 'mobx';
import { useGuestPrintInstruction } from 'features/eiq-secure/hooks/use-guest-license-print';
import { RouteLocation } from 'features/eiq-secure/services/route-for-print';

interface IEiqSecureGuestLicenseProps {
  guestLicenseAdmit: boolean;
}

const LICENSE_PRINT_ID = 'license-print-id';

export const guestEntryTypeOptions = [
  {
    label: entryTypeMap[EntryType.Driver],
    value: EntryType.Driver,
    isApproved: false,
  },
  {
    label: entryTypeMap[EntryType.Pedestrian],
    value: EntryType.Pedestrian,
    isApproved: false,
  },
  {
    label: entryTypeMap[EntryType.Passenger],
    value: EntryType.Passenger,
    isApproved: false,
  },
];

export const EiqSecureGuestLicense = observer(({ guestLicenseAdmit }: IEiqSecureGuestLicenseProps) => {
  const { isMobileOrTable } = useMedia();
  const tableContext = useTableContext();
  const [selectedAction, setSelectedAction] = useState<string>(Admit);
  const [turnbackReason, setTurnbackReason] = useState<string>();
  const [isSwitchChecked, setIsSwitchChecked] = useState(false);
  const [password, setPassword] = useState<string>('');
  const [keyOptions, setKeyOptions] = useState<{ value: string; label: string }[]>([]);
  const [isButtonsDisabled, setIsButtonsDisabled] = useState(true);
  const [selectedGate, setSelectedGate] = useState<string>();
  const [licensePlate, setLicensePale] = useState<string>();
  const [isDrivingPrivilagesRevoked, setDrivingPrivilagesRevoked] = useState(false);

  const idDocumentRef = useRef<any>(null);

  const [form] = Form.useForm();
  const entryTypeValue = Form.useWatch('entryType', form);

  const isKeyPermissionAllowed = eiqSecureStore.selectedGuest?.isKeyPermissionAllowed;

  const { defaultGate, defaultLicenseState, defaultLicensePlate, image, camera } = useDefaultAdmitValues({
    currentGuest: eiqSecureStore.selectedGuest,
  });

  useBoundPlateRecognition(() => {
    const guestPlate = eiqSecureStore.selectedGuest?.carLicensePlates.find((i) => i.isPrimary);
    const fallbackState = guestPlate?.state || configurationStore.configuration?.default.defaultLicenseState;
    form.setFieldValue('carLicensePlate', guestPlate?.number || '');
    form.setFieldValue('carState', fallbackState);
    form.setFieldValue('gate', userAuthStore.defaultGate || undefined);
    const values = form.getFieldsValue();
    handleOnValuesChange(null, values);
  });

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey });

  const gateOptions = useMemo(() => toGateOptions(configurationStore?.configuration?.gates || []), []);

  const defaultEntryType = useMemo(() => {
    const lprContext = plateRecognitionContext.data;

    if (lprContext) {
      return EntryType.Driver;
    }

    return guestEntryTypeOptions?.find((i) => i.isApproved)?.value || guestEntryTypeOptions?.[0]?.value;
  }, []);

  const data = useMemo(() => {
    return [eiqSecureStore.selectedGuest];
  }, [eiqSecureStore.selectedGuest]);

  let columns = eiqSecureGuestInitialColumns;

  if (!featureStore.isDriveIdEnabled) {
    columns = columns.filter((i) => i.key !== 'idDocument');
  }

  // Table config
  const tableControls = useTableControls({
    clientSide: true,
    data: data,
    initialColumns: filterColumnsByPersonalization(columns, EIQ_SECURE_GUEST_LIST_TABLE_NAME),
  });

  useEffect(() => {
    if (isSwitchChecked) {
      const filters = `address_id: ${eiqSecureStore.selectedAddressId}`;
      addressesKeyService.getKeys({ offset: 0, limit: 20 }, filters, '').then((result) => {
        const options = result?.items?.map((item) => ({
          value: item.keyNumber.toString(),
          label: `${item.keyNumber.toString()} (${item.name})`,
          disabled: item.status === 'out',
        }));
        setKeyOptions(options);
      });
    } else setKeyOptions([]);
  }, [isSwitchChecked]);

  useEffect(() => {
    if (guestLicenseAdmit) handleClickAdmit();
  }, [guestLicenseAdmit]);

  const updateGuestAndResidentDetails = () => {
    residentsService
      .getResidentDetails(eiqSecureStore.selectedResident?.id!)
      .then((data) => {
        for (const address of data.addresses) {
          const foundGuest = address.guests.find((guest) => guest.id === eiqSecureStore.selectedGuest?.id);
          if (foundGuest) {
            eiqSecureStore.setSelectedGuest(foundGuest);
            break;
          }
        }
        tableControls.updateSelectedRow(data);
        eiqSecureStore.setResidentDetails(data);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const addIdDocument = async (residentId: number, addressId: number, guestId: number) => {
    const idDocument = idDocumentRef.current?.getIdDocument();
    const selectedGuest = eiqSecureStore.selectedGuest!;

    if (!idDocument) {
      return { url: selectedGuest.idDocument?.imageUrl, type: selectedGuest.idDocument?.type };
    }

    const documentNameFileName = `${idDocument.firstName}_${idDocument.firstName}_${idDocument.type}.jpg`;
    const file = base64ToFile(idDocument.imageBase64, documentNameFileName);

    let idDocumentUrl = '';

    if (file.size === 0) {
      notification.warning({ message: `File = ${documentNameFileName}, length = 0`, duration: 10 });
    } else {
      const uploadResultl = await accountService.uploadPhoto(file, UploadFileTypes.IdDocument);
      idDocumentUrl = uploadResultl.absoluteUri;
    }

    const requestData: IAddIdDocument = {
      type: idDocument.type,
      imageUrl: idDocumentUrl,
      firstName: idDocument.firstName,
      lastName: idDocument.lastName,
      secondaryLastName: idDocument.secondaryLastName,
      gender: idDocument.gender,
      dateOfBirth: idDocument.dateOfBirth,
      nationality: idDocument.nationality,
      address: idDocument.address,
      issuingCountry: idDocument.issuingCountry,
      issuingCountryIso: idDocument.issuingCountryIso,
      number: idDocument.number,
      additionalNumber: idDocument.additionalNumber,
      expiredAt: idDocument.expiredAt,
      issuedAt: idDocument.issuedAt,
    };

    const result = await guestService.addIdDocument(residentId, addressId, guestId, requestData);

    eiqSecureStore.setSelectedGuest({ ...selectedGuest, idDocument: result });

    return { url: result.imageUrl, type: result.type };
  };

  const onFinish = async (values: any) => {
    const { carLicensePlate, carState, entryType } = values;
    const plateRecognitionId = plateRecognitionContext.getRecognitionId(values.gate, carLicensePlate, carState);

    const residentId = eiqSecureStore?.residentDetails?.id;
    const selectedAddressId = eiqSecureStore?.selectedAddressId;
    const selectedGuestId = eiqSecureStore?.selectedGuest?.id;

    if (!residentId || !selectedAddressId || !selectedGuestId) {
      return;
    }

    const idDocument = await addIdDocument(residentId, selectedAddressId, selectedGuestId);

    const admitGuestBody = {
      idDocumentUrl: idDocument.url,
      idDocumentType: idDocument.type ? idDocument.type : null,
      entryType,
      carLicensePlate,
      carState,
      image: image,
      camera: camera,
      driverName: entryType === EntryType.Passenger ? `${values.driverFirstName ?? ''} ${values.driverLastName ?? ''}`.trim() : null,
      driverCompanyName: entryType === EntryType.Passenger ? values.driverCompanyName : null,
      ...values,
      plateRecognitionId: plateRecognitionId,
      isDrivingPrivilagesRevoked: isDrivingPrivilagesRevoked,
    };

    if (selectedAction === Admit) {
      if (password) {
        admitGuestBody.key.password = password;
      }
      return guestService
        .admitGuest(residentId, selectedAddressId, selectedGuestId, admitGuestBody)
        .then(() => {
          notification.success({ message: 'Guest has been admitted' });
          updateGuestAndResidentDetails();
          tableContext.setSelectedRow(null);
          eiqSecureStore.setAdmitFlow(null);
          eiqSecureStore.setIsAdmitFlowProcessed();
          setResidentListSearchFilter('');
          plateRecognitionContext.clear();
        })
        .catch((error) => {
          if (error.response.status === 403) {
            notification.error({ message: 'Password is invalid' });
          }
        });
    } else {
      admitGuestBody.reason = turnbackReason;
      return guestService.turnbackGuest(residentId, selectedAddressId, selectedGuestId, admitGuestBody).then(() => {
        notification.success({ message: 'The guest has been turned back' });
        updateGuestAndResidentDetails();
        tableContext.setSelectedRow(null);
        eiqSecureStore.setAdmitFlow(null);
        eiqSecureStore.setIsAdmitFlowProcessed();
        plateRecognitionContext.clear();
        setResidentListSearchFilter('');
      });
    }
  };

  const handleOnValuesChange = (_: any, values: any) => {
    if (!values.gate) {
      setIsButtonsDisabled(true);
      return;
    }

    if (values.entryType === EntryType.Driver && (!values.carLicensePlate || !values.carState)) {
      setIsButtonsDisabled(true);
      return;
    }

    if (isSwitchChecked && (!values.key.guestPhoneNumber || !values.key.keyNumbers?.length)) {
      setIsButtonsDisabled(true);
      return;
    }
    setSelectedGate(values.gate);
    setLicensePale(values.carLicensePlate);
    setIsButtonsDisabled(false);
  };

  useEffect(() => {
    if (!form) {
      setIsButtonsDisabled(true);
      return;
    }
    const values = form.getFieldsValue();
    handleOnValuesChange(null, values);
  }, [form, isSwitchChecked, entryTypeValue]);

  useEffect(() => {
    return () => {
      form.resetFields();
    };
  }, []);

  const admitGuest = () => {
    setSelectedAction(Admit);
    form.submit();
  };

  const handleClickAdmit = async () => {
    const licensePlate = form.getFieldValue('carLicensePlate');

    if (featureStore.isCitationsEnabled) {
      const result = await trackPromise(citationService.isLicensePlateDrivingPrivilegesRevoked(licensePlate));
      setDrivingPrivilagesRevoked(result.isDrivingPrivilegesRevoked);

      if (result.isDrivingPrivilegesRevoked) {
        const initData = {
          drivingPrivilageData: result,
          isVacantAddress: false,
        };

        drivingPrivilagesRevokeModal.setModalInitData(initData);
        drivingPrivilagesRevokeModal.showModal();
        return;
      }
    }

    admitGuest();
  };

  const drivingPrivilagesRevokeModal = useModal({
    onClose: (isSuccess, type) => {
      if (isSuccess) {
        if (type == 'admit') {
          admitGuest();
        }
        if (type == 'turnback') {
          turnbackModal.showModal();
        }
      }
    },
  });

  const contactsModal = useModal();

  const turnbackModal = useModal({
    onClose: (isSuccessSaved, turnbackReason) => {
      if (isSuccessSaved) {
        setSelectedAction(TurnBack);
        setTurnbackReason(turnbackReason);
        form.submit();
      }
    },
  });

  const provideKeyModal = useModal({
    onClose: (isSuccessSaved, password) => {
      if (isSuccessSaved) {
        setPassword(password);
        setIsSwitchChecked(true);
      } else {
        setIsSwitchChecked(false);
        setPassword('');
      }
    },
  });

  const collectKeyModal = useModal({
    onClose: (isSuccessSaved) => {
      if (isSuccessSaved) updateGuestAndResidentDetails();
    },
  });

  const guest = tableControls.getSelectedRow() as IGuestRead | undefined;
  const gate = configurationStore.configuration?.gates.find((item) => item.name === selectedGate);
  const address = tableContext.getSelectedRow()?.address;
  const { city, state } = configurationStore.communitySettings ?? {};

  const hasGateCoordinates = gate?.latitude && gate?.longitude;

  const origin: RouteLocation | undefined = hasGateCoordinates
    ? {
        lat: gate.latitude!,
        lng: gate.longitude!,
      }
    : undefined;

  const destination = address && city && state ? `${state}, ${city}, ${formatAddressLabel(address)}` : undefined;

  const { printRef, route, fastAccessPassId, handlePrint } = useGuestPrintInstruction({
    isMapsLoaded: isLoaded,
    origin,
    destination,
    guestId: guest?.id,
    fastAccessEnabled: featureStore.isFastAccessPrintDirectionEnabled,
    obtainFastAccessPass: (guestId) => fastAccessService.obtainGuestFastAccessPass(guestId),
  });

  const admitBtn = (
    <Button type="primary" className="primary-btn mobile-btn" icon={<AdmitIcon />} onClick={handleClickAdmit} disabled={isButtonsDisabled}>
      Approve
    </Button>
  );

  const admitActions = (
    <Space size={8} className="buttons-font-16">
      <Button type="default" icon={<PrintIcon />} size={'middle'} onClick={handlePrint} className="mobile-btn" disabled={isButtonsDisabled}>
        Print directions
      </Button>
      <Button
        icon={<KeyIcon />}
        onClick={collectKeyModal.showModal}
        disabled={!eiqSecureStore.selectedGuest?.issuedKeys.length}
        className="mobile-btn">
        Collect key
      </Button>
      <Button className="important-btn mobile-btn" icon={<TurnbackIcon />} onClick={turnbackModal.showModal} disabled={isButtonsDisabled}>
        Turnback
      </Button>
      <Button className="secondary-btn mobile-btn" icon={<PhoneIcon />} onClick={contactsModal.showModal}>
        Dial
      </Button>
      {admitBtn}
    </Space>
  );

  const getGuestName = () => {
    const guest = tableControls.getSelectedRow() as IGuestRead;

    if (isServiceGuest(guest?.type)) {
      return guest?.companyName;
    } else {
      return `${guest?.firstName} ${guest?.lastName}`;
    }
  };

  const onEntryTypeChanged = async (newValue: EntryType) => {
    if (newValue === EntryType.Passenger) {
      form.setFieldValue('carLicensePlate', '');
      form.setFieldValue('carState', defaultLicenseState);
    }
    if (newValue === EntryType.Driver) {
      form.setFieldValue('carLicensePlate', defaultLicensePlate);
      form.setFieldValue('carState', defaultLicenseState);
    }
  };

  const updateFromIdDocument = (idDocumentFirstName?: string | null, idDocumnetLastName?: string | null) => {
    const entryType = form.getFieldValue('entryType');

    const isCaptureDriverName = configurationStore.configuration?.driveIdConfiguration?.isCaptureDriverName;

    if (entryType === EntryType.Passenger && isCaptureDriverName) {
      form.setFieldValue('driverFirstName', idDocumentFirstName);
      form.setFieldValue('driverLastName', idDocumnetLastName);
    } else {
      form.setFieldValue('firstName', idDocumentFirstName);
      form.setFieldValue('lastName', idDocumnetLastName);
    }

    const values = form.getFieldsValue();
    handleOnValuesChange(null, values);
  };

  const onIdDocumentRecognized = async (idDocument: IIdDocumentData) => {
    updateFromIdDocument(idDocument.firstName, idDocument.lastName);
  };

  useEffect(() => {
    const dispose = reaction(
      () => eiqSecureStore.selectedGuest?.idDocument,
      (idDocument) => {
        if (!idDocument) {
          return;
        }

        updateFromIdDocument(idDocument.firstName, idDocument.lastName);
      },
    );

    return () => dispose();
  }, []);

  const isInfoSectionEnabled = () => featureStore.isDriveIdEnabled || featureStore.isPlateScanEnabled;

  const onUpdateFromCameraClicked = () => {
    const entryType = form.getFieldValue('entryType');

    const isCaptureDriverName = configurationStore.configuration?.driveIdConfiguration?.isCaptureDriverName;

    if (entryType === EntryType.Passenger && isCaptureDriverName) {
      form.setFieldValue('driverFirstName', '');
      form.setFieldValue('driverLastName', '');
    } else {
      form.setFieldValue('firstName', '');
      form.setFieldValue('lastName', '');
    }

    const values = form.getFieldsValue();
    handleOnValuesChange(null, values);

    if (eiqSecureStore.selectedGuest) {
      eiqSecureStore.setSelectedGuest({ ...eiqSecureStore.selectedGuest, idDocument: null });
    }
  };

  const onProvideKeyClicked = (checked: boolean) => {
    if (checked) {
      provideKeyModal.showModal();
    } else {
      setIsSwitchChecked(false);
      setPassword('');
    }
  };

  return (
    <div>
      <EiqSecureGuestPrintInstruction
        id={LICENSE_PRINT_ID}
        ref={printRef}
        address={tableContext.getSelectedRow()?.address}
        gate={selectedGate}
        guestLicense={licensePlate}
        guestName={getGuestName()}
        rules={configurationStore.configuration?.default.communityRules}
        communityName={configurationStore.configuration?.default.communityName ?? ''}
        routeOriginAddressString={route?.addressFrom}
        route={route}
        isMinimize={(route?.steps.length ?? 0) > 7}
        guest={tableControls.getSelectedRow()}
        fastAccessPassId={fastAccessPassId}
        speedLimit={configurationStore.configuration?.default.speedLimit}
        speedLimitImageUrl={configurationStore.configuration?.default?.speedLimitImageUrl}
      />
      <TableContextProvider controls={tableControls}>
        <div className="gust-license">
          <Row justify="space-between">
            <Col>
              <div className="space-12">
                <div className="breadcrumb-wrapper">
                  <Button
                    icon={<BackIcon />}
                    onClick={() => {
                      eiqSecureStore.setAdmitFlow(isMobileOrTable ? AdmitGuestEnum.SELECT_GUEST : null);
                    }}>
                    Back
                  </Button>
                  <Breadcrumb
                    separator="–"
                    items={[
                      {
                        title: ' Guests',
                        onClick: () => {
                          eiqSecureStore.setAdmitFlow(isMobileOrTable ? AdmitGuestEnum.SELECT_GUEST : null);
                        },
                      },
                      {
                        title: 'License',
                      },
                    ]}
                  />
                </div>
                <span className="sub-title">{formatAddressLabel(eiqSecureStore.getSelectedAddress())}</span>
              </div>
            </Col>
            <Col>{!isMobileOrTable && admitActions}</Col>
          </Row>

          <TableBase selectFirstRowByDefault minHeight={'80px'} />

          <div className="container-row">
            <Row gutter={32}>
              {isInfoSectionEnabled() && (
                <Col lg={8} sm={24} className="instructions-wrapper">
                  {featureStore.isDriveIdEnabled && (
                    <DriveId
                      onUpdateFromCamera={onUpdateFromCameraClicked}
                      onImageRecognized={onIdDocumentRecognized}
                      ref={idDocumentRef}
                      idDocument={eiqSecureStore.selectedGuest?.idDocument}
                    />
                  )}
                  {featureStore.isPlateScanEnabled && image && (
                    <div className="instructions-item">
                      <span className="title">PlateScan ({camera})</span>
                      <Image src={image} alt="License Plate" loading="lazy" />
                    </div>
                  )}
                </Col>
              )}
              <Col lg={isInfoSectionEnabled() ? 16 : 24} xs={24}>
                <FormWrapper form={form} onFinish={onFinish} layout="vertical" onValuesChange={handleOnValuesChange}>
                  <Row gutter={{ lg: 32, md: 16, xs: 0 }}>
                    <Col lg={isInfoSectionEnabled() ? 14 : 18} xs={24}>
                      <Row gutter={21}>
                        <Col lg={isInfoSectionEnabled() ? 24 : 12} xs={24}>
                          {isServiceGuest(eiqSecureStore.selectedGuest?.type) && (
                            <Form.Item
                              name="companyName"
                              label="Company name"
                              initialValue={eiqSecureStore.selectedGuest?.companyName}
                              rules={eiqSecureGuestLicenseValidation.carState}>
                              <Input placeholder="Enter company name" />
                            </Form.Item>
                          )}
                          <Form.Item name="firstName" label="First name" initialValue={eiqSecureStore.selectedGuestFirstName}>
                            <Input placeholder="Enter first name" />
                          </Form.Item>
                          <Form.Item name="lastName" label="Last name" initialValue={eiqSecureStore.selectedGuestLastName}>
                            <Input placeholder="Enter last name" />
                          </Form.Item>
                          <Form.Item name="entryType" label="Entry Type" initialValue={defaultEntryType}>
                            <Select
                              options={guestEntryTypeOptions}
                              placeholder="Select Entry Type"
                              popupClassName="so-select"
                              onChange={onEntryTypeChanged}
                            />
                          </Form.Item>

                          {entryTypeValue === EntryType.Passenger && (
                            <>
                              <Form.Item name="driverFirstName" label="Driver first name">
                                <Input placeholder="Enter driver first name" autoFocus />
                              </Form.Item>
                              <Form.Item name="driverLastName" label="Driver last name">
                                <Input placeholder="Enter driver last name" />
                              </Form.Item>
                              <Form.Item
                                name="driverCompanyName"
                                label="Driver company name"
                                rules={eiqSecureGuestLicenseValidation.companyName}>
                                <Input placeholder="Enter driver company name" />
                              </Form.Item>
                            </>
                          )}
                        </Col>

                        <Col lg={isInfoSectionEnabled() ? 24 : 12} xs={24}>
                          {(entryTypeValue === EntryType.Driver || entryTypeValue === EntryType.Passenger) && (
                            <>
                              <Form.Item
                                name="carState"
                                label="License State"
                                initialValue={defaultLicenseState}
                                rules={eiqSecureGuestLicenseValidation.carState}>
                                <SelectState placeholder="Select a license state" popupClassName="so-select" />
                              </Form.Item>
                              <Form.Item
                                name="carLicensePlate"
                                initialValue={defaultLicensePlate}
                                label="License Plate"
                                rules={eiqSecureGuestLicenseValidation.carLicensePlate}>
                                <Input placeholder="Enter license plate" autoFocus />
                              </Form.Item>
                            </>
                          )}
                          <Form.Item
                            name="gate"
                            label="Location (Gate)"
                            rules={eiqSecureGuestLicenseValidation.gate}
                            initialValue={defaultGate}>
                            <Select options={gateOptions} placeholder="Select Location (Gate)" popupClassName="so-select" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>
                    {isKeyPermissionAllowed && (
                      <Col lg={isInfoSectionEnabled() ? 10 : 6} sm={24}>
                        {isKeyPermissionAllowed && (
                          <Form.Item label="Provide key">
                            <Switch onClick={onProvideKeyClicked} checked={isSwitchChecked} />
                          </Form.Item>
                        )}
                        {isSwitchChecked && (
                          <>
                            <InputPhoneFormItem
                              initPhoneNumner={eiqSecureStore.selectedGuest?.phone}
                              initCountryCode={eiqSecureStore.selectedGuest?.countryCode}
                              label="Phone number"
                              numberFieldName={['key', 'guestPhoneNumber']}
                              countryCodeFieldName={['key', 'guestPhoneCountryCode']}
                            />
                            <Form.Item name={['key', 'keyNumbers']} label="Select key" rules={eiqSecureGuestLicenseValidation.keyNumber}>
                              <Checkbox.Group options={keyOptions} className="vertical-radio-group"></Checkbox.Group>
                            </Form.Item>
                          </>
                        )}
                      </Col>
                    )}
                  </Row>
                </FormWrapper>
              </Col>
            </Row>
          </div>
        </div>
      </TableContextProvider>
      <KeyActionModal title="Collect" isOpen={collectKeyModal?.isOpenModal} onClose={collectKeyModal?.hideModal} />
      <KeyActionModal title="Provide" isOpen={provideKeyModal?.isOpenModal} onClose={provideKeyModal?.hideModal} />
      <ContactsModal title="Contacts" isOpen={contactsModal?.isOpenModal} onClose={contactsModal?.hideModal} />
      <TurnbackModal title="Turnback" isOpen={turnbackModal?.isOpenModal} onClose={turnbackModal?.hideModal} />
      <DrivingPrivilegesRevokeModal
        isOpen={drivingPrivilagesRevokeModal?.isOpenModal}
        onClose={drivingPrivilagesRevokeModal?.hideModal}
        initData={drivingPrivilagesRevokeModal?.initData}
      />
      {isMobileOrTable && <Footer className="guest-license-footer">{admitActions}</Footer>}
    </div>
  );
});
